import { computed } from "vue";
import storageService from "@/services/storage.service";
import { pushModal } from "jenesius-vue-modal";
import PublicSignInDialog from "@/views/private/PublicSignInDIalog.vue";
import utilService from "@/services/util.service";
import { TYPE } from "vue-toastification";

/**
 * Composable for authentication checks and public sign-in functionality
 */
export const useAuth = () => {
  /**
   * Check if user is logged in
   * @returns {boolean} True if user has valid token and user data
   */
  const isLoggedIn = computed(() => {
    const token = storageService.getToken();
    const user = storageService.getUser();
    return !!(token && user && user._id);
  });

  /**
   * Check authentication status synchronously
   * @returns {boolean} True if user is logged in
   */
  const checkAuth = (): boolean => {
    const token = storageService.getToken();
    const user = storageService.getUser();
    return !!(token && user && user._id);
  };

  /**
   * Show public sign-in modal if user is not authenticated
   * @param {Object} options - Optional configuration for the modal
   * @param {boolean} options.fromHeader - Whether the modal is triggered from header
   * @param {boolean} options.fromPostDetail - Whether the modal is triggered from post detail
   * @param {Object} options.postData - Post data if applicable
   * @returns {Promise<void>}
   */
  const showPublicSignIn = async (options: {
    fromHeader?: boolean;
    fromPostDetail?: boolean;
    postData?: any;
  } = {}) => {
    const modal = await pushModal(PublicSignInDialog, {
      fromHeader: options.fromHeader ?? true,
      fromPostDetail: options.fromPostDetail ?? false,
      postData: options.postData,
    });
    modal.onclose = () => {
      console.log("Sign-in dialog closed");
    };
  };

  /**
   * Require authentication - shows sign-in modal if not logged in
   * @param {Function} callback - Function to execute if user is authenticated
   * @param {Event} event - Optional event to stop propagation
   * @param {Object} modalOptions - Optional configuration for the modal
   * @returns {Promise<void>}
   */
  const requireAuth = async (
    callback: () => void | Promise<void>,
    event?: Event,
    modalOptions?: {
      fromHeader?: boolean;
      fromPostDetail?: boolean;
      postData?: any;
    }
  ): Promise<void> => {
    if (event) {
      try {
        event.stopPropagation();
      } catch (e) {
        // Ignore errors
      }
    }

    if (!checkAuth()) {
      await showPublicSignIn({
        fromHeader: modalOptions?.fromHeader,
        fromPostDetail: modalOptions?.fromPostDetail,
        postData: modalOptions?.postData,
      });
      return;
    }

    await callback();
  };

  return {
    isLoggedIn,
    checkAuth,
    showPublicSignIn,
    requireAuth,
  };
};

